import React, { useCallback, useEffect, useMemo, useState } from "react";
import Button from "../../Buttons/Button";
import * as XLSX from "xlsx";
import { PDFDocument, StandardFonts, rgb } from "pdf-lib";
import { Gender } from "../../../assets/data/enumData";
import { dateFormator, getEmployeeId } from "../../../utils";
import ColumnSelector from "./ColumnSelector";
import PageTitle from "../../Common/PageTitle";
import LoopFieldsSet from "../../FormFields/LoopFieldsSet";
import useFormHandler from "../../Validations/useFormHandler";
import { v4 as uuidv4 } from "uuid";
import FormFieldSet from "../../FormFields/FormFieldSet";
import { determineColumnDataType } from "./tableUtils";
import getFilterOperatorsByDataType from "./tableUtils/filterOperators";

const TableDownloadModalData = ({
  downloadType,
  columns,
  rows,
  selectedRows = [],
  currentPage,
  rowsPerPage,
  onClose,
}) => {
  const colunmsCleanKeys = columns
    .filter((column) => column.key || column.labelKey)
    .map((column) => ({
      key: column.key,
      label: column.label,
      labelKey: column.labelKey || null,
      dataType: column.dataType || null,
    }));
  const [isColumnsAndFilterOpen, setIsColumnsAndFilterOpen] = useState(false);
  const [selectedColumns, setSelectedColumns] = useState(colunmsCleanKeys);

  const initialFilterConditions = [
    {
      id: uuidv4(),
      filterColunm: "",
      operator: "",
      value: [],
      dataType: "string",
    },
  ];

  const initialAnyConditions = [
    {
      id: uuidv4(),
      filterColunm: "",
      operator: "",
      value: [],
      dataType: "string",
    },
  ];

  const { formData, errors, handleChange, validateAllFields, setFormData } =
    useFormHandler({
      filterConditions: initialFilterConditions,
      anyConditions: initialAnyConditions,
    });

    let selectedData = rows.filter((row) => selectedRows.includes(row.id));
//   let selectedData = rows.filter((row) => {
//   const key = row.uiid ? row.uiid : row.id;
//   return selectedRows.includes(key);
// });


  let slicedData = rows.slice(
    (currentPage - 1) * rowsPerPage,
    (currentPage - 1) * rowsPerPage + rowsPerPage
  );

  let finalData = selectedRows?.length ? selectedData : slicedData;
  const [filteredFinalData, setFilteredFinalData] = useState(finalData);

  const statusOptions = [
    { label: "Active", value: 1 },
    { label: "Inactive", value: 2 },
    { label: "Pending", value: 3 },
    { label: "Resign", value: 5 },
  ];
  const EmploymentType = [
    {
      value: 1,
      label: "Regular",
    },
    { value: 2, label: "Consultant" },
    { value: 3, label: "Contractor" },
  ];

  function normalizeFilterValue(condition) {
    // only for status column convert to number
    if (condition.filterColunm === "status") {
      const found = statusOptions.find(
        (o) =>
          o.label.toLowerCase() === String(condition.filterValue).toLowerCase()
      );
      if (found) return found.value;
    }
    if (condition.filterColunm === "employmentType") {
      const found = EmploymentType.find(
        (o) =>
          o.label.toLowerCase() === String(condition.filterValue).toLowerCase()
      );
      if (found) return found.value;
    }

    return condition.filterValue;
  }

  const applyFilterConditions = useCallback(
    (rowsToFilter, filterObj, columns) => {
      if (!filterObj) return rowsToFilter;

      const { filterConditions = [], anyConditions = [] } = filterObj;

      const getCellValue = (row, column) => {
        if (column.valueGetter) return column.valueGetter(row);
        return row[column.key];
      };

      const checkCondition = (condition, row) => {
        const { operator, dataType = "string" } = condition;

        const filterValue = normalizeFilterValue(condition);

        const column = columns.find(
          (col) => (col.labelKey || col.key) === condition.filterColunm
        );
        if (!column) return true;

        const cellValue = getCellValue(row, column);

        // DATE
        if (dataType === "date" && filterValue) {
          const cellDate = new Date(cellValue).getTime();
          const filterDate = new Date(filterValue).getTime();

          switch (operator) {
            case "equal":
              return cellDate === filterDate;
            case "notEqual":
              return cellDate !== filterDate;
            case "greaterThan":
              return cellDate > filterDate;
            case "lessThan":
              return cellDate < filterDate;
            case "greaterThanOrEqual":
              return cellDate >= filterDate;
            case "lessThanOrEqual":
              return cellDate <= filterDate;
            default:
              return true;
          }
        }

        // NUMBER
        if (dataType === "number" || dataType === "integer") {
          switch (operator) {
            case "equal":
              return Number(cellValue) == Number(filterValue);
            case "notEqual":
              return Number(cellValue) != Number(filterValue);
            case "greaterThan":
              return Number(cellValue) > Number(filterValue);
            case "lessThan":
              return Number(cellValue) < Number(filterValue);
            case "greaterThanOrEqual":
              return Number(cellValue) >= Number(filterValue);
            case "lessThanOrEqual":
              return Number(cellValue) <= Number(filterValue);
            default:
              return true;
          }
        }

        // STRING default
        switch (operator) {
          case "equal":
            return (
              String(cellValue).toLowerCase() ===
              String(filterValue).toLowerCase()
            );
          case "notEqual":
            return (
              String(cellValue).toLowerCase() !==
              String(filterValue).toLowerCase()
            );
          case "contains":
            return String(cellValue)
              .toLowerCase()
              .includes(String(filterValue).toLowerCase());
          case "notContains":
            return !String(cellValue)
              .toLowerCase()
              .includes(String(filterValue).toLowerCase());
          default:
            return true;
        }
      };

      // 1) AND Conditions
      let finalRows = rowsToFilter.filter((row) =>
        filterConditions.every((c) => checkCondition(c, row))
      );

      // 2) OR Conditions
      if (anyConditions.length > 0) {
        finalRows = finalRows.filter((row) =>
          anyConditions.some((c) => checkCondition(c, row))
        );
      }

      return finalRows;
    },
    [columns]
  );
  //   useEffect(() => {
  //     if (
  //       formData &&
  //       (formData.filterConditions?.length || formData.anyConditions?.length)
  //     ) {
  //       finalData = applyFilterConditions(
  //         finalData,
  //         {
  //           filterConditions: formData.filterConditions,
  //           anyConditions: formData.anyConditions,
  //         },
  //         columns
  //       );
  //     }
  //   }, [formData]);

  useEffect(() => {
    let tempData = selectedRows?.length ? selectedData : slicedData;

    if (
      formData &&
      (formData.filterConditions?.length || formData.anyConditions?.length)
    ) {
      tempData = applyFilterConditions(
        tempData,
        {
          filterConditions: formData.filterConditions,
          anyConditions: formData.anyConditions,
        },
        columns
      );
    }

    setFilteredFinalData(tempData);
  }, [formData, rows, selectedRows, currentPage, rowsPerPage]);

  const handleDownload = async () => {
    // if formData filters exist -> apply filtering locally for download only

    const type = downloadType.toLowerCase();

    // Build headers dynamically from columns.label
    const headers = selectedColumns.map((col) => col.label);

    const data = filteredFinalData.map((row) => {
      const mappedRow = {};
      selectedColumns.forEach((col) => {
        // Handle special fields (custom formatting)
        switch (col.key) {
          case "fullName":
            mappedRow[col.label] = `${row.fullName?.firstName || ""} ${
              row.fullName?.middleName || ""
            } ${row.fullName?.lastName || ""}`.trim();
            break;
          case "hobby":
            mappedRow[col.label] = Array.isArray(row.hobby)
              ? row.hobby.join(", ")
              : row.hobby || "";
            break;
          case "gender":
            mappedRow[col.label] =
              Gender.find((g) => g.value === row.gender)?.label || row.gender;
            break;

          case "status":
            mappedRow[col.label] =
              statusOptions.find((s) => s.value === row.status)?.label ||
              row.status;
            break;
          case "employmentType":
            mappedRow[col.label] =
              EmploymentType.find((s) => s.value === row.employmentType)
                ?.label || row.employmentType;
            break;

          case "joindedDate":
            mappedRow[col.label] = dateFormator
              ? dateFormator(row.joindedDate)
              : row.joindedDate || "";
            break;
          case "joinDate":
            mappedRow[col.label] = dateFormator
              ? dateFormator(row.joinDate)
              : row.joinDate || "";
            break;
          case "salaryNew":
            mappedRow[col.label] = `$${row.salary ?? 0}`;
            break;
          case "code":
            mappedRow[col.label] = getEmployeeId(row.uiid, row.employmentType);
            break;

          case "email":
            mappedRow[col.label] = row.email?.primary
              ? row.email?.primary
              : row.email || "";
            break;

          case "alternateEmailId":
            mappedRow[col.label] = row.email?.secondary || "";
            break;
          case "location":
            mappedRow[col.label] = row.location.country || "";
            break;

          case "cityState":
            mappedRow[col.label] = row.location.state || "";
            break;

          case "fullAddress":
            mappedRow[col.label] = row.location.address1 || "";
            break;

          default:
            mappedRow[col.label] = row[col.key] ?? "";
        }
      });
      return mappedRow;
    });

    // CSV Download
    if (type === "csv") {
      const csvRows = [
        headers.join(","), // header row
        ...data.map((item) =>
          headers
            .map((h) => `"${(item[h] ?? "").toString().replace(/"/g, '""')}"`)
            .join(",")
        ),
      ];
      const csvContent = csvRows.join("\n");
      const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", "tableData.csv");
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    }

    //Excel Download
    else if (type === "excel") {
      const ws = XLSX.utils.json_to_sheet(data);
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, "Table Data");
      XLSX.writeFile(wb, "tableData.xlsx");
    }

    //PDF Export using pdf-lib
    else if (type === "pdf") {
      const pdfDoc = await PDFDocument.create();
      let page = pdfDoc.addPage([842, 595]); // A4 landscape
      const font = await pdfDoc.embedFont(StandardFonts.Helvetica);

      const { height, width } = page.getSize();
      const margin = 40;
      let y = height - margin;

      //Title
      const title = `Data of ${
        selectedRows.length ? selectedRows.length : rowsPerPage
      } Records`;

      page.drawText(title, {
        x: margin,
        y,
        size: 14,
        font,
        color: rgb(0, 0, 0),
      });
      y -= 30;

      //Table header
      const headerFontSize = 10;
      const rowFontSize = 9;
      const cellPadding = 5;
      const columnWidth = (width - margin * 2) / headers.length;

      //Draw header background first
      headers.forEach((header, i) => {
        const x = margin + i * columnWidth;

        // Header background (dark gray)
        page.drawRectangle({
          x,
          y: y - 3,
          width: columnWidth,
          height: 18,
          color: rgb(0.2, 0.2, 0.2),
        });

        // Header text (white)
        page.drawText(header, {
          x: x + cellPadding,
          y: y,
          size: headerFontSize,
          font,
          color: rgb(1, 1, 1),
        });
      });

      y -= 25;

      //Data rows
      data.forEach((row) => {
        headers.forEach((header, i) => {
          const x = margin + i * columnWidth;
          const text = String(row[header] ?? "").slice(0, 25); // prevent overflow
          page.drawText(text, {
            x: x + cellPadding,
            y,
            size: rowFontSize,
            font,
            color: rgb(0, 0, 0),
          });
        });

        y -= 18;

        //Add new page if overflow
        if (y < margin + 30) {
          page = pdfDoc.addPage([842, 595]);
          y = height - margin;

          // Re-draw header on each new page
          headers.forEach((header, i) => {
            const x = margin + i * columnWidth;
            page.drawRectangle({
              x,
              y: y - 3,
              width: columnWidth,
              height: 18,
              color: rgb(0.2, 0.2, 0.2),
            });
            page.drawText(header, {
              x: x + cellPadding,
              y,
              size: headerFontSize,
              font,
              color: rgb(1, 1, 1),
            });
          });
          y -= 25;
        }
      });

      // Save and download
      const pdfBytes = await pdfDoc.save();
      const blob = new Blob([pdfBytes], { type: "application/pdf" });
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = "tableData.pdf";
      link.click();
    }
    onClose();
  };
  const renderConditionFields = (condition, index, isAllConditions = true) => {
    const conditionsArray = isAllConditions
      ? formData.filterConditions
      : formData.anyConditions;

    return (
      <>
        <FormFieldSet
          title="Column"
          placeholder="Select Column"
          type="select"
          options={columns
            .filter((col) => col.key)
            .map((col) => ({
              label: col.label,
              value: col.labelKey || col.key,
            }))}
          value={condition.filterColunm || ""}
          onChange={(value) => {
            conditionsArray[index].filterColunm = value;
            conditionsArray[index].filterValue = "";
            conditionsArray[index].operator = "";

            const selectedColumn =
              columns.find((col) => (col.labelKey || col.key) === value) || {};

            determineColumnDataType(
              selectedColumn,
              rows,
              conditionsArray[index],
              null
            );

            setFormData({ ...formData });
          }}
        />
        <div>
          <FormFieldSet
            title="Operator"
            placeholder="Select Operator"
            type="select"
            options={getFilterOperatorsByDataType(
              conditionsArray[index].dataType
            )}
            value={condition.operator || ""}
            onChange={(value) => {
              conditionsArray[index].operator = value;
              setFormData({ ...formData });
            }}
            disabled={!conditionsArray[index].dataType}
          />
        </div>
        <div className="w-full">
          <FormFieldSet
            title="Value"
            placeholder="Enter value"
            type={(() => {
              const dataType = conditionsArray[index].dataType;

              switch (dataType) {
                case "date":
                  return "datepicker";
                case "boolean":
                  return "select";
                case "enum":
                  return "select";
                case "array":
                  return "select";
                case "number":
                case "integer":
                case "currency":
                case "percentage":
                  return "text";
                case "email":
                  return "text";
                case "url":
                  return "text";
                case "phone":
                  return "phone";
                default:
                  return "text";
              }
            })()}
            options={(() => {
              const dataType = conditionsArray[index].dataType;
              const selectedColumn =
                columns.find(
                  (col) =>
                    (col.labelKey || col.key) ===
                    conditionsArray[index].filterColunm
                ) || {};

              if (dataType === "enum" && selectedColumn.dataValue) {
                return selectedColumn.dataValue;
              }

              if (dataType === "array") {
                if (Array.isArray(selectedColumn.dataValue)) {
                  return selectedColumn.dataValue.map((item) => {
                    if (
                      item &&
                      typeof item === "object" &&
                      "label" in item &&
                      "value" in item
                    ) {
                      return item;
                    }

                    const itemStr = String(item);
                    const valueStr = itemStr.replace(/\s+/g, "_");

                    return {
                      label: itemStr,
                      value: valueStr,
                    };
                  });
                }
                return selectedColumn.dataValue;
              }

              if (dataType === "boolean") {
                return [
                  { label: "True", value: true },
                  { label: "False", value: false },
                ];
              }

              return [];
            })()}
            value={
              condition.filterValue ||
              (conditionsArray[index].dataType === "array" ? [] : "")
            }
            onChange={(value) => {
              conditionsArray[index].filterValue = value;
              setFormData({ ...formData });
            }}
            disabled={!conditionsArray[index].operator}
            multiple={conditionsArray[index].dataType === "array"}
          />
        </div>
      </>
    );
  };

  const addFilterCondition = () => {
    const newCondition = {
      id: uuidv4(),
      filterColunm: "",
      operator: "",
      value: [],
      dataType: "string",
    };
    setFormData({
      ...formData,
      filterConditions: [...formData.filterConditions, newCondition],
    });
  };

  const removeFilterCondition = (id) => {
    setFormData({
      ...formData,
      filterConditions: formData.filterConditions.filter(
        (condition) => condition.id !== id
      ),
    });
  };

  // Add/Remove functions for Any Conditions (OR logic)
  const addAnyCondition = () => {
    const newCondition = {
      id: uuidv4(),
      filterColunm: "",
      operator: "",
      value: [],
      dataType: "string",
    };
    setFormData({
      ...formData,
      anyConditions: [...formData.anyConditions, newCondition],
    });
  };

  const removeAnyCondition = (id) => {
    setFormData({
      ...formData,
      anyConditions: formData.anyConditions.filter(
        (condition) => condition.id !== id
      ),
    });
  };

  return (
    <div className="flex flex-col items-center py-6">
      {/* Header */}
      <p className="text-lg font-medium text-center">
        Total {filteredFinalData?.length} rows selected to download
      </p>

      {/* Advanced Settings */}
      <div className="mt-6">
        <Button
          title="Advanced Settings"
          color="black"
          className="py-1"
          onClick={() => {
            setIsColumnsAndFilterOpen(true);
          }}
        />
      </div>
      {isColumnsAndFilterOpen && (
        <>
          <div className=" mt-5">
            <ColumnSelector
              columns={colunmsCleanKeys}
              placeholder={"Select Required Columns"}
              rounded={"rounded"}
              showSearch
              selectedColumns={selectedColumns}
              onChange={(cols) => {
                setSelectedColumns(cols);
              }}
              // column selection should always be editable (no need to click edit)
              disabled={false}
            />
          </div>
          <div className="flex flex-col flex-1 gap-3 overflow-auto mt-5">
            {/* All Conditions - AND Logic */}
            <div className="border border-gray-200 p-5 rounded-md">
              <PageTitle title="Search In" tag="h6" />
              <LoopFieldsSet
                title="All Conditions"
                data={formData.filterConditions}
                onAdd={addFilterCondition}
                onRemove={removeFilterCondition}
                minItems={1}
                maxItems={5}
                gridCols="grid-cols-[repeat(3,1fr)_0.5fr]"
                renderFields={(condition, index) =>
                  renderConditionFields(condition, index, true)
                }
                // allow editing conditions directly
                disabled={false}
              />
            </div>

            {/* Any Conditions - OR Logic */}
            <div className="border border-gray-200 p-5 rounded-md">
              <PageTitle title="Search In" tag="h6" />
              <LoopFieldsSet
                title="Any Condition"
                data={formData.anyConditions}
                onAdd={addAnyCondition}
                onRemove={removeAnyCondition}
                minItems={1}
                maxItems={5}
                gridCols="grid-cols-[repeat(3,1fr)_0.5fr]"
                renderFields={(condition, index) =>
                  renderConditionFields(condition, index, false)
                }
                disabled={false}
              />
            </div>
          </div>
        </>
      )}

      {/* Action Buttons */}
      <div className="flex justify-center gap-6 mt-10">
        <Button
          title="Cancel"
          variant="outlined"
          className="py-1"
          onClick={onClose}
        />
        <Button
          title="Download"
          className="py-1"
          onClick={handleDownload}
          disabled={filteredFinalData?.length <= 0}
        />
      </div>
    </div>
  );
};

export default TableDownloadModalData;
